"use client";
import { useSession } from "next-auth/react";
import {
  useRouter,
  useSearchParams,
  type ReadonlyURLSearchParams,
} from "next/navigation";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { TRACKING_ID_KEY } from "~/components/analytics/useTrackingId";
import { LoadingPage } from "~/components/loading";
import { StepIdType, StepType } from "~/components/orderdraft/orderSteps";
import { generateUUID } from "~/lib/generateUUID";
import {
  ChildInfo,
  DraftOrderUpdate,
  NameValidationResponse,
  Order,
  OrderBilling,
  OrderFormDataDetails,
  VoucherValidationResult,
} from "~/types";

import useOrderInitialization from "~/components/orderdraft/hooks/useOrderInitialization";
import useOrderNavigation from "~/components/orderdraft/hooks/useOrderNavigation";
import useStoryGeneration from "~/components/orderdraft/hooks/useStoryGeneration";


export type FrontEndOrderFormData = OrderFormDataDetails;

export type FrontEndOrderDataAndSetter = {
  formData: FrontEndOrderFormData;
  setFormData: React.Dispatch<React.SetStateAction<FrontEndOrderFormData>>;
};

type OrderDataContextType = FrontEndOrderDataAndSetter & {
  isLoading: boolean;
  order: Order | null;
  billing: OrderBilling | null;
  anonymousId: string | null;
  previousChildren: ChildInfo[];
  steps: StepType[];
  currentStep: StepType | null;
  validatingStory: boolean;
  nameValidationResponse: NameValidationResponse | null;
  validatingName: boolean;
  isNextLoading: boolean;
};

type OrderActionsContextType = {
  isStepValid: (stepId: StepIdType) => boolean;
  isCurrentStepValid: () => boolean;
  getNextStepPath: () => string | null;
  nextStep: () => Promise<boolean>;
  prevStep: () => void;
  addStoryMessage: (
    message: string,
    shouldReplaceLastUserMessage?: boolean,
  ) => Promise<boolean>;
  applyVoucher: (voucherValidation: VoucherValidationResult) => Promise<void>;
  removeVoucher: () => Promise<void>;
  saveFormData: (formData: FrontEndOrderFormData) => Promise<void>;
};

const OrderDataContext = createContext<OrderDataContextType | undefined>(
  undefined,
);
const OrderActionsContext = createContext<OrderActionsContextType | undefined>(
  undefined,
);

function getOrderIdFromSearchParams(
  searchParams: ReadonlyURLSearchParams,
): number | null {
  const orderIdParam = searchParams.get("orderId");
  if (!orderIdParam) return null;

  const orderId = parseInt(orderIdParam, 10);
  return isNaN(orderId) ? null : orderId;
}

function getAnonymousIdFromUrl(): string | null {
  if (typeof window === "undefined") return null;
  const urlParams = new URLSearchParams(window.location.search);
  const queryParam = urlParams.get("orderAid");
  return !!queryParam ? queryParam : null;
}

function getOrCreateAnonymousId(): string | null {
  if (typeof window === "undefined") return null;

  const urlParamId = getAnonymousIdFromUrl();
  if (urlParamId) {
    return urlParamId;
  }

  let anonymousId = localStorage.getItem(TRACKING_ID_KEY);
  if (!anonymousId) {
    anonymousId = generateUUID();
    localStorage.setItem(TRACKING_ID_KEY, anonymousId);
  }
  return anonymousId;
}

export function OrderFormProvider({ children }: { children: ReactNode }) {
  const { status: authStatus } = useSession();
  const router = useRouter();
  const searchParams = useSearchParams();

  const [anonymousId] = useState<string | null>(() => getOrCreateAnonymousId());
  const orderIdSearchParam = getOrderIdFromSearchParams(searchParams);

  const {
    isLoading,
    order,
    setOrder,
    formData,
    setFormData,
    previousChildren,
  } = useOrderInitialization({
    authStatus,
    anonymousId,
    orderIdSearchParam,
    router,
  });

  const saveFormData = useCallback(
    async (formDataToSave: FrontEndOrderFormData) => {
      const draftOrderUpdate: DraftOrderUpdate = {
        formData: {
          ...formDataToSave,
          childName: formDataToSave.childName?.trim() || "",
          story: formDataToSave.story?.trim(),
        },
      };

      if (authStatus === "authenticated") {
        try {
          const { customerOrdersService } =
            await import("~/connectors/orders.connector");
          const updatedDraftOrder =
            await customerOrdersService.upsertDraft(draftOrderUpdate);
          setOrder(updatedDraftOrder);
          setFormData(updatedDraftOrder.formData || {});
        } catch (error) {
          console.error("Error saving draft order:", error);
        }
      } else if (authStatus === "unauthenticated" && anonymousId) {
        try {
          const { customerOrdersService } =
            await import("~/connectors/orders.connector");
          const updatedAnonymousDraft =
            await customerOrdersService.upsertAnonymousDraft(
              anonymousId,
              draftOrderUpdate.formData ?? {},
            );
          if (updatedAnonymousDraft && updatedAnonymousDraft.data) {
            setFormData(updatedAnonymousDraft.data as FrontEndOrderFormData);
          }
        } catch (error) {
          console.error("Error saving anonymous draft order:", error);
        }
      } else {
        throw new Error(
          "Cannot save form data: unknown authentication status or missing anonymous ID.",
        );
      }
    },
    [authStatus, anonymousId, setFormData, setOrder],
  );

  const {
    steps,
    currentStep,
    isNextLoading,
    nextStep,
    prevStep,
    getNextStepPath,
    isStepValid,
    isCurrentStepValid,
    nameValidationResponse,
    validatingName,
  } = useOrderNavigation({
    authStatus,
    anonymousId,
    order,
    setOrder,
    formData,
    previousChildren,
    saveFormData,
    isLoading,
  });

  const { validatingStory, addStoryMessage } = useStoryGeneration({
    authStatus,
    anonymousId,
    formData,
    setFormData,
    setOrder,
  });

  const applyVoucher = useCallback(
    async (voucherValidation: VoucherValidationResult) => {
      const voucher = voucherValidation.voucher;
      if (!voucher)
        throw new Error("No voucher information provided for application.");
      const { customerOrdersService } =
        await import("~/connectors/orders.connector");
      const draftOrder = await customerOrdersService.upsertDraft({
        voucherCode: voucher.code,
      });
      setOrder(draftOrder);
    },
    [setOrder],
  );

  const removeVoucher = useCallback(async () => {
    const { customerOrdersService } =
      await import("~/connectors/orders.connector");
    const draftOrder = await customerOrdersService.upsertDraft({
      voucherCode: null,
    });
    setOrder(draftOrder);
  }, [setOrder]);

  const billing: OrderBilling | null = useMemo(() => {
    if (!order) return null;
    return {
      subtotal: order.subtotal,
      discountAmount: order.discountAmount,
      total: order.total,
      currency: order.currency,
      voucherId: order.voucherId,
      voucherCode: order.voucherCode,
    };
  }, [order]);

  const data: OrderDataContextType = {
    isLoading,
    formData,
    setFormData,
    order,
    billing,
    anonymousId,
    previousChildren,
    steps,
    currentStep,
    validatingStory,
    nameValidationResponse,
    validatingName,
    isNextLoading,
  };

  const actions: OrderActionsContextType = {
    isStepValid,
    isCurrentStepValid,
    getNextStepPath,
    nextStep,
    prevStep,
    addStoryMessage,
    applyVoucher,
    removeVoucher,
    saveFormData,
  };

  return (
    <OrderDataContext.Provider value={data}>
      <OrderActionsContext.Provider value={actions}>
        {isLoading ? <LoadingPage /> : children}
      </OrderActionsContext.Provider>
    </OrderDataContext.Provider>
  );
}

export function useOrderData() {
  const ctx = useContext(OrderDataContext);
  if (ctx === undefined)
    throw new Error("useOrderData must be used within an OrderFormProvider");
  return ctx;
}

export function useOrderActions() {
  const ctx = useContext(OrderActionsContext);
  if (ctx === undefined)
    throw new Error("useOrderActions must be used within an OrderFormProvider");
  return ctx;
}

export function useOrderForm() {
  const d = useOrderData();
  const a = useOrderActions();
  return { ...d, ...a } as FrontEndOrderDataAndSetter &
    OrderDataContextType &
    OrderActionsContextType;
}
